import pathToRegexp from 'path-to-regexp';
import apiConfig from '../infrastructure/Config';
import _ from 'lodash';
import { compose, withProps } from 'recompose';
import { connect } from 'react-redux';
import { withLocale } from '../hocs/withLocale';

const route = (path) => {
  let toPath = pathToRegexp.compile(path);
  return (routeParams) => {
    if (routeParams === undefined) return path;

    const pathKeys = pathToRegexp.parse(path)
      .filter(token => typeof token === 'object')
      .map(token => token.name);

    const pathParams = {};
    const queryParams = {};

    Object.entries(routeParams).forEach(([key, value]) => {
      if (pathKeys.includes(key)) {
        pathParams[key] = value;
      } else {
        queryParams[key] = value;
      }
    });

    let url = toPath(pathParams);

    const query = new URLSearchParams(queryParams).toString();
    if (query) {
      url += `?${query}`;
    }

    return url;
  };
};

const routes = {
  root: route('/'),
  prices: route('/prices'), 
  faq: route('/faq'),
  pricesWithCulture: route('/prices/:culture'),
  privacyPolicy: route('/privacy-policy'),
  termsAndConditions: route('/terms-and-conditions'),
  styleGuide: route('/style-guide'),
  search: route('/search'),
  seoSearch: route('/search/:country/:city'),
  seoSearchPromo: route('/search/:country/:city/promocode'),
  seoSearchCategory: route('/search/:country/:city/:category'),
  createStudio: route('/new-studio'),
  studioId: route('/studio/id/:id'),
  studio: route('/studio/:domainName'),
  booking: route('/studio/:domainName/booking/:courseId'),
  groupBooking: route('/studio/:domainName/group-booking/:courseId'),
  courseBooking: route('/studio/:domainName/course-booking/:courseId'),
  joinUs: route('/join-us'),
  login: route('/login'),
  clientLogin: route('/client-login'),
  redirectLogin: route('/redirect-login/:token'),
  loginToOwner: route('/login/:ownerId'),
  bizNotification: route('/biz-notification'),
  connect: route('/connect/:ownerId/:studioClientId'),
  inShoes: route('/in-shoes/:email'),
  inOwnerShoes: route('/in-shoes/:ownerId/:email'),
  invite: route('/invite/:token'),
  inviteTeacher: route('/invite-teacher/:ownerId/:teacherId'),
  fb: route('/fb'),
  myStudios: route('/myStudios'),
  pendingClients: route('/pending-clients'),
  pendingClientsById: route('/pending-clients/:funnelId'),
  pendingClientAtFunnel: route('/pending-clients/:funnelId/:id'),
  pendingClient: route('/pending-client/:id'),
  funnel: route('/funnel/:key/:tab?'),
  newFunnel: route('/new-funnel'),
  funnelWebhookSettings: route('/funnel/webhook/settings/:key'),
  pendingClientExtraFieldSettings: route(
    '/pending-client/extra-field/setting/'
  ),
  funnelDictionarySettings: route('/funnel-dictionary/settings/:funnelId'),
  myClients: route('/myClients'),
  editSchedule: route('/studio/id/:id/schedule'),
  editCourses: route('/studio/id/:id/schedule/courses'),
  newCourse: route('/studio/id/:id/schedule/course'),
  editCourse: route('/studio/id/:id/schedule/course/:courseId'),
  deletedCourse: route('/studio/id/:id/schedule/deleted-course/:courseId'),
  newClass: route('/studio/id/:id/schedule/course/:courseId/class'),
  editClass: route('/studio/id/:id/schedule/course/:courseId/class/:classId'),
  editStudio: route('/studio/id/:id/edit'),
  editPrices: route('/studio/id/:id/prices'),
  editRooms: route('/studio/id/:id/rooms'),
  dashboard: route('/studio/id/:id/dashboard'),
  qrScanner: route('/studio/id/:id/qr-scanner'),
  notifications: route('/business-notification'),
  editNotifications: route('/studio/id/:id/notifications/'),
  newNotification: route('/studio/id/:studioId/notification/'),
  editNotification: route('/studio/id/:studioId/notification/:id'),
  ratePlans: route('/rate-plans'),
  billingTags: route('/billing-tags'),
  paymentSettings: route('/payment-setting'),
  matrix: route('/matrix'),
  addRatePlan: route('/new-rate-plans'),
  editRatePlan: route('/rate-plans/:id'),
  billing: route('/billing'),
  studioBilling: route('/billing/:studioId'),
  userBilling: route('/billing'),
  paymentReport: route('/payment-report'),
  paymentReportChart: route('/payment-report-chart'),
  organizationSettings: route('/organization-settings'),
  subscriptionBilling: route('/organization-settings/user-subscription-billing'),
  subscription: route('/user-subscription'),
  blocked: route('/blocked'),
  forum: route('/forum'),
  ai: route('/ai'),
  taskView: route('/forum/task/:taskId'),
  debitCreditReport: route('/debit-credit-report'),
  myExpenses: route('/my-expenses'),
  mobileNewExpense: route('/new-expense'),
  mobileNewClient: route('/newClient'),
  myClient: route('/myClients/:clientId'),
  qrClient: route('/client/qr/:clientId'),
  qrStudioClient: route('/studio-client/qr/:studioClientId'),
  mobileAttendance: route('/myClients/:clientId/attendance'),
  mobileNewClientPayment: route('/myClients/:clientId/newPayment'),
  myClasses: route('/myClasses'),
  studioClientClasses: route('/client/:ownerId/:studioClientId'),
  studioClientClassesGelios: route('/client'),
  ownerPaymentInfo: route('/payment-info/:ownerId/:studioClientId'),
  ownerTermsAndConditionsHtml: route('/:ownerId/terms-and-conditions-html'),
  unsubscribe: route('/client/un,subscribe/:ownerId/:clientId'),
  me: route('/me'),
  studioRegistrationsId: route('/studio/id/:id/registrations'),
  school: route('/studio/id/:id/school'),
  studioRegistrations: route('/studio/:domainName/registrations'),
  studioRegistrationId: route('/studio/id/:id/registrations/:registrationId'),
  studioRegistration: route(
    '/studio/:domainName/registrations/:registrationId'
  ),
  classId: route('/studio/id/:id/class/:groupId'),
  bookingRatePlan: route('/booking-rate-plan/:ownerId/:ratePlanId'),
  class: route('/studio/:domainName/class/:groupId'),
  verifyGuest: route('/verifyGuest/:token'),
  clientAttendance: route('/attendance/:groupId/:id'),
  cancelClientAttendance: route('/cancel-attendance/:groupId/:id'),
  pendingReview: route('/review-invite/:id'),
  addPaymentToAttendance: route('/attendance/:groupId/:id/add-payment'),
  ratePlanReport: route('/client-rate-plans'),
  myAccount: route('/myAccount'),
  teacherAccount: route('/teacher-account'),
  changePassword: route('/change-password'),
  signUp: route('/signUp'),
  confirm: route('/confirm/:token'),
  forgetPassword: route('/forget-password'),
  forgetTeacherPassword: route('/forget-teacher-password/:ownerId'),
  resetTeacherPassword: route('/resetPassword/:ownerId/:token'),
  resetPassword: route('/resetPassword/:token'),
  changeEmail: route('/changeEmail/:token/:email'),
  changeTeacherEmail: route('/change-teacher-email/:ownerId/:token/:email'),
  myTeachers: route('/my-teachers'),
  messaging: route('/messaging'),
  messageBatch: route('/message-batch/:id'),
  askQuestion: route('/messaging/ask-question'),
  askQuestionPublic: route('/messaging/ask-question/:chatId'),
  myTeacher: route('/teacher/:id'),
  landing: route('/landing'),
  aboutUs: route('/about-us'),
  applyBiz: route('/apply'),
  loginBiz: route('/login'),
  applyBizWithCulture: route('/apply/:culture'),
  scheduleWidget: route('/schedule-widget/:domainName'),
  courseWidget: route('/course-widget/:domainName/:course'),
  classWidget: route('/class-widget/:domainName/:groupId'),
  games: route('/games'),
  liveGame: route('/games/:gameId'),
  homeworks: route('/studio/:studioId/homeworks'),
  homeworkDetail: route('/studio/:studioId/homeworks/:homeworkId'),
  finances: route('/finances'),

  getStudioLink: (studio) => {
    if (studio.domainName) {
      return routes.studio({ domainName: studio.domainName });
    } else {
      if (studio.id) {
        return routes.studioId({ id: studio.id });
      } else {
        return routes.studioId({ id: studio._id });
      }
    }
  },
  getBizStudioLink: (studio) => {
    if (studio.id) {
      return routes.editStudio({ id: studio.id });
    } else {
      return routes.editStudio({ id: studio._id });
    }
  },
  getStudioAbsolutLink: (domain, studio) => {
    if (domain && studio) {
      return `${domain.href}${routes.getStudioLink(studio)}`;
    }
  },
  getShareInviteAbsoluteLink: (user, domain) => {
    return `${apiConfig.bizUrl}${routes.applyBizWithCulture({
      culture: domain.defaultCulture,
    })}?ref=${user._id}`;
  },
  getHomeLink: () => {
    return apiConfig.webAppUrl;
  },
  getClassLink: (studio, groupId) => {
    if (studio.domainName) {
      return routes.class({ domainName: studio.domainName, groupId: groupId });
    } else {
      return routes.classId({ id: studio.id, groupId: groupId });
    }
  },
  getTheClassLink: (studio, groupId, domain) => {
    return `${domain.href}${routes.getClassLink(studio, groupId)}`;
  },
  getStudioClientLink: (domain, ownerId, studioClientId) => {
    return `${domain.href}${routes.studioClientClasses({
      ownerId: ownerId,
      studioClientId: studioClientId,
    })}`;
  },
  getStudioRegistrationsLink: (studio) => {
    return routes.studioRegistrationsId({ id: studio.id });
  },
  getRegistrationLink: (studioId, registrationId) => {
    return routes.studioRegistrationId({
      id: studioId,
      registrationId: registrationId,
    });
  },
  business: route('/business'),
  businessWithCulture: route('/business/:culture'),
  landings: {
    acting: route('/acting-classes'),
    language: route('/language-classes'),
    fitness: route('/fitness-classes'),
    music: route('/music-classes'),
    tutoring: route('/tutoring'),
    preschool: route('/preschool'),
    childcare: route('/childcare'),
    skiSchool: route('/ski-snowboarding-school'),
    crossfit: route('/crossfit'),
  },
};

export default routes;

export const absoluteRoutes = compose(
  connect(({ user }) => ({ user })),
  withLocale,
  withProps(({ user, currentLanguage }) => {
    const bizUrl = (route) => (params) => `${apiConfig.bizUrl}${route(params)}`;
    const rootUrl = (route) => (params) => {
      const domain =
        user && user.clientDomain && user.clientDomain.href
          ? user.clientDomain.href
          : apiConfig.rootUrl[params['culture']];
      return `${domain}${route(params)}`;
    };
    return {
      absolutePath: {
        root: rootUrl(routes.root),
        business: rootUrl(routes.business),
        aboutUs: rootUrl(routes.aboutUs),
        prices: () =>
          rootUrl(routes.pricesWithCulture)({ culture: currentLanguage }),
        faq: rootUrl(routes.faq),
        privacyPolicy: rootUrl(routes.privacyPolicy),
        termsAndConditions: () =>
          rootUrl(routes.termsAndConditions)({ culture: currentLanguage }),

        landings: _.reduce(
          Object.keys(routes.landings).map((name) => ({
            name,
            url: rootUrl(routes.landings[name]),
          })),
          (res, { name, url }) => {
            res[name] = url;
            return res;
          },
          {}
        ),

        applyBiz: () =>
          bizUrl(routes.applyBizWithCulture)({ culture: currentLanguage }),
        loginBiz: bizUrl(routes.loginBiz),
      },
    };
  })
);
